import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import WorkspaceNotice from '../components/WorkspaceNotice';
import { workspaceAccessMessage } from '../lib/workspaceAccess';

function ResourceRow({ resource, included, used, pending, available }) {
  return (
    <tr className="border-b border-zinc-100 last:border-0">
      <td className="px-4 py-3 font-medium">{resource}</td>
      <td className="px-4 py-3 font-mono text-xs text-zinc-600">{included}</td>
      <td className="px-4 py-3 font-mono text-xs text-zinc-600">{used}</td>
      <td className="px-4 py-3 font-mono text-xs text-zinc-600">{pending}</td>
      <td className="px-4 py-3 font-mono text-xs text-zinc-600">{available}</td>
    </tr>
  );
}

export default function Billing() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    setError(null);
    api.get('/usage')
      .then(({ data }) => {
        if (cancelled) return;
        setData(data);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(workspaceAccessMessage(err, 'Billing data could not be loaded.'));
      });
    return () => { cancelled = true; };
  }, [isAdmin]);

  if (!isAdmin) {
    return (
      <WorkspaceNotice
        testId="billing-denied"
        title="Billing unavailable"
        message="Only organization admins can manage billing."
        detail="Ask an organization admin to review your workspace role."
      />
    );
  }

  if (error) {
    return (
      <section data-testid="billing-error" className="border border-red-200 bg-red-50 p-6 text-red-800">
        <p className="text-xs font-mono uppercase tracking-widest text-red-500">Billing</p>
        <h1 className="mt-1 font-display text-2xl font-bold tracking-tight text-red-950">Billing unavailable</h1>
        <p className="mt-3 max-w-2xl text-sm leading-6 text-red-800">{error}</p>
      </section>
    );
  }

  if (!data) return <p className="text-sm text-zinc-500" data-testid="billing-loading">Loading...</p>;
  const { pricing, usage } = data;
  const freeVerifications = pricing.free_verifications_included || pricing.free_included;
  const freeSignatures = pricing.free_signatures_included || 10;

  return (
    <div data-testid="billing-page" className="space-y-6">
      <div>
        <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Insights</p>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Plan &amp; limits</h1>
      </div>

      <section className="border border-zinc-200 rounded-sm bg-white p-6" data-testid="current-plan">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Current plan</p>
            <h2 className="font-display text-2xl font-bold tracking-tight mt-1">Free tier</h2>
            <p className="text-sm text-zinc-600 mt-2 max-w-2xl">
              Your workspace includes monthly usage limits for verifications and document signatures. When a limit is reached,
              new links cannot be created until usage becomes available again.
            </p>
          </div>
          <div data-testid="billing-status"
               className="text-xs font-mono uppercase tracking-widest border border-emerald-300 bg-emerald-50 text-emerald-700 px-2 py-1">
            Active
          </div>
        </div>
      </section>

      <section className="border border-zinc-200 rounded-sm overflow-x-auto bg-white" data-testid="limits-card">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-xs uppercase tracking-widest text-zinc-500 text-left">
              <th className="px-4 py-3 font-medium">Resource</th>
              <th className="px-4 py-3 font-medium">Included</th>
              <th className="px-4 py-3 font-medium">Used</th>
              <th className="px-4 py-3 font-medium">Pending</th>
              <th className="px-4 py-3 font-medium">Available</th>
            </tr>
          </thead>
          <tbody>
            <ResourceRow
              resource="Verifications"
              included={freeVerifications}
              used={usage.total_completed}
              pending={usage.verifications_pending || 0}
              available={usage.verifications_available ?? usage.free_remaining}
            />
            <ResourceRow
              resource="Signatures"
              included={freeSignatures}
              used={usage.signatures_used}
              pending={usage.signatures_pending || 0}
              available={usage.signatures_available ?? usage.signatures_free_remaining}
            />
          </tbody>
        </table>
      </section>

      <p className="text-sm text-zinc-500">Need higher limits? Additional plans will be available soon.</p>
    </div>
  );
}
